import { Component, DestroyRef, Inject, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ContextoBackend, TipoContexto } from '@app/models/auth/contexto-backend';
import { AuthService } from '@app/services/auth/auth.service';
import { ServicioSesion } from '@app/services/sesion/sesion.service';

export interface SelectRolDialogData {
  contextos: ContextoBackend[];
}

interface GrupoConLogo {
  id: string;
  nombre: string;
  logo: string | null;
  contextos: ContextoBackend[];
}

type Paso = 'tipo' | 'opciones';

const MAX_EQUIPOS_EN_GRID = 6;

@Component({
  selector: 'app-select-rol',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatSelectModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './select-rol.component.html',
  styleUrls: ['./select-rol.component.scss'],
})
export class SelectRolDialogComponent {
  private _authService = inject(AuthService);
  private _sesion = inject(ServicioSesion);
  private _destroyRef = inject(DestroyRef);

  public cargando = signal(false);
  public paso = signal<Paso>('tipo');
  public tipoSeleccionado = signal<TipoContexto | null>(null);

  public tiposDisponibles: TipoContexto[] = [];
  public globales: ContextoBackend[] = [];
  public ligas: GrupoConLogo[] = [];
  public equipos: GrupoConLogo[] = [];

  public readonly maxEquiposEnGrid = MAX_EQUIPOS_EN_GRID;
  public equipoFiltroControl = new FormControl('');
  public equiposFiltrados: GrupoConLogo[] = [];

  private _logosConError = new Set<string>();

  constructor(
    public dialogRef: MatDialogRef<SelectRolDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SelectRolDialogData
  ) {
    // Muestra de inmediato lo que ya se tenía en caché, y en paralelo pide la
    // lista fresca al backend — así nunca hace falta darle refresh a mano.
    this.procesarContextos(data.contextos);
    this.actualizarContextos();

    this.equipoFiltroControl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(texto => {
      const busqueda = (texto ?? '').trim().toLowerCase();
      this.equiposFiltrados = busqueda
        ? this.equipos.filter(e => e.nombre.toLowerCase().includes(busqueda))
        : this.equipos;
    });
  }

  private procesarContextos(contextos: ContextoBackend[]): void {
    this.globales = contextos.filter(c => c.tipo === 'global');
    this.ligas = this._agrupar(contextos, 'liga', c => c.liga_id!, c => c.liga_nombre, c => c.liga_logo);
    this.equipos = this._agrupar(contextos, 'equipo', c => c.team_id!, c => c.equipo_nombre, c => c.equipo_escudo);
    this.equiposFiltrados = this.equipos;

    this.tiposDisponibles = [];
    if (this.globales.length) this.tiposDisponibles.push('global');
    if (this.ligas.length) this.tiposDisponibles.push('liga');
    if (this.equipos.length) this.tiposDisponibles.push('equipo');

    this.equipoFiltroControl.reset();
    this._logosConError.clear();

    if (this.tiposDisponibles.length === 1) {
      this.tipoSeleccionado.set(this.tiposDisponibles[0]);
      this.paso.set('opciones');
    } else {
      this.tipoSeleccionado.set(null);
      this.paso.set('tipo');
    }
  }

  private _agrupar(
    contextos: ContextoBackend[],
    tipo: TipoContexto,
    idDe: (c: ContextoBackend) => string,
    nombreDe: (c: ContextoBackend) => string | null,
    logoDe: (c: ContextoBackend) => string | null
  ): GrupoConLogo[] {
    const mapa = new Map<string, GrupoConLogo>();
    contextos
      .filter(c => c.tipo === tipo)
      .forEach(c => {
        const id = idDe(c);
        if (!mapa.has(id)) {
          mapa.set(id, { id, nombre: nombreDe(c) ?? tipo, logo: logoDe(c), contextos: [] });
        }
        mapa.get(id)!.contextos.push(c);
      });
    return Array.from(mapa.values())
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));
  }

  public elegirTipo(tipo: TipoContexto): void {
    this.tipoSeleccionado.set(tipo);
    this.paso.set('opciones');
  }

  public volver(): void {
    this.tipoSeleccionado.set(null);
    this.paso.set('tipo');
  }

  public elegirContexto(ctx: ContextoBackend): void {
    this.dialogRef.close(ctx);
  }

  public logoConError(id: string): boolean {
    return this._logosConError.has(id);
  }

  public onLogoError(id: string): void {
    this._logosConError.add(id);
  }

  public actualizarContextos(): void {
    this.cargando.set(true);
    this._authService.getContextos().subscribe({
      next: (res) => {
        this._sesion.guardarContextos(res.contextos);
        this.procesarContextos(res.contextos);
        this.cargando.set(false);
      },
      error: () => this.cargando.set(false),
    });
  }
}
